import type { NotificationSettings, WaitlistStatus } from "@kaidoki/proto";
import { Check, ChevronRight, Mail, RefreshCw, User } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import {
  deleteAccount,
  getNotificationSettings,
  getWaitlistStatus,
  updateNotificationSettings,
} from "../api/client";
import { ScreenHeader } from "../components/AppSurface";
import { BottomSheet } from "../components/BottomSheet";
import {
  AppleIcon,
  GoogleIcon,
  LineIcon,
} from "../components/icons/ProviderIcons";
import { useGroup } from "../contexts/GroupContext";
import { useSubscription } from "../contexts/SubscriptionContext";
import { trackLanguageChange } from "../lib/analytics";
import { getUserInfo, logout } from "../lib/auth";

const LANGUAGES = [
  { code: "ja", label: "日本語" },
  { code: "en", label: "English" },
  { code: "es", label: "Español" },
  { code: "it", label: "Italiano" },
  { code: "zh", label: "中文" },
];

const ALL_PROVIDERS = [
  { key: "line", label: "LINE", icon: LineIcon },
  { key: "google", label: "Google", icon: GoogleIcon },
  { key: "apple", label: "Apple", icon: AppleIcon },
  { key: "email", labelKey: "settings:account.emailProvider", icon: Mail },
] as const;

// 通知時間として選択可能な時間帯（6〜23時）
const SELECTABLE_HOURS = Array.from({ length: 18 }, (_, i) => i + 6);

const LINE_FRIEND_ADD_URL = "https://line.me/R/ti/p/@464vaqfa";

export function Settings() {
  const { t, i18n } = useTranslation(["settings", "common"]);
  const navigate = useNavigate();
  const user = getUserInfo();
  const { groups, clearGroups } = useGroup();
  const { isPro, authProviders } = useSubscription();

  const [notifSettings, setNotifSettings] =
    useState<NotificationSettings | null>(null);
  const [notifLoading, setNotifLoading] = useState(true);
  const [notifError, setNotifError] = useState(false);
  const [notifUpdating, setNotifUpdating] = useState(false);

  const [waitlist, setWaitlist] = useState<WaitlistStatus | null>(null);

  const [showLineFriendGuide, setShowLineFriendGuide] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [hoursSaving, setHoursSaving] = useState(false);

  const fetchNotificationSettings = useCallback(async () => {
    setNotifLoading(true);
    setNotifError(false);
    try {
      const data = await getNotificationSettings();
      setNotifSettings(data);
    } catch {
      setNotifError(true);
    } finally {
      setNotifLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNotificationSettings();
  }, [fetchNotificationSettings]);

  useEffect(() => {
    if (!isPro) {
      getWaitlistStatus()
        .then(setWaitlist)
        .catch(() => {});
    }
  }, [isPro]);

  async function handleToggle(
    field: "pushEnabled" | "lineEnabled" | "emailEnabled",
    value: boolean,
  ) {
    if (!notifSettings || notifUpdating) return;
    const prev = notifSettings;
    setNotifSettings({ ...notifSettings, [field]: value });
    setNotifUpdating(true);
    try {
      const updated = await updateNotificationSettings({ [field]: value });
      setNotifSettings(updated);
      if (field === "lineEnabled" && value) {
        setShowLineFriendGuide(true);
      }
    } catch {
      setNotifSettings(prev);
    } finally {
      setNotifUpdating(false);
    }
  }

  async function toggleHour(hour: number) {
    if (!notifSettings || hoursSaving) return;
    const currentHours = notifSettings.preferredHours;
    let newHours: number[];
    if (currentHours.includes(hour)) {
      if (currentHours.length <= 1) return;
      newHours = currentHours.filter((h) => h !== hour);
    } else {
      newHours = [...currentHours, hour].sort((a, b) => a - b);
    }
    const prev = notifSettings;
    setNotifSettings({ ...notifSettings, preferredHours: newHours });
    setHoursSaving(true);
    try {
      const updated = await updateNotificationSettings({
        preferredHours: newHours,
      });
      setNotifSettings(updated);
    } catch {
      setNotifSettings(prev);
    } finally {
      setHoursSaving(false);
    }
  }

  function handleLanguageChange(langCode: string) {
    i18n.changeLanguage(langCode);
    trackLanguageChange(langCode);
  }

  async function handleLogout() {
    await logout();
    clearGroups();
    navigate("/", { replace: true });
  }

  async function handleDeleteAccount() {
    if (isDeleting) return;
    setIsDeleting(true);
    try {
      await deleteAccount();
      localStorage.removeItem("kaidoki_token");
      localStorage.removeItem("kaidoki_user");
      localStorage.removeItem("kaidoki_user_id");
      localStorage.removeItem("kaidoki_onboarding_done");
      clearGroups();
      navigate("/", { replace: true });
    } finally {
      setIsDeleting(false);
    }
  }

  return (
    <div className="min-h-dvh bg-white">
      <ScreenHeader
        title={t("settings:title")}
        onBack={() => navigate("/home")}
      />

      <main className="mx-auto max-w-2xl space-y-5 px-4 py-5 sm:px-6">
        {/* アカウント */}
        <SectionHeader>{t("settings:account.title")}</SectionHeader>
        <section className="overflow-hidden rounded-[20px] border border-[#e3e8f0] bg-white">
          <div className="flex items-center gap-3 px-4 py-4">
            {user?.pictureUrl ? (
              <img
                src={user.pictureUrl}
                alt={user.displayName}
                className="h-12 w-12 rounded-full"
              />
            ) : (
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#edf3ff]">
                <User size={20} className="text-[#285bbf]" />
              </div>
            )}
            <div>
              <p className="text-sm font-semibold text-gray-900">
                {user?.displayName ?? t("settings:account.lineUser")}
              </p>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {ALL_PROVIDERS.map(({ key, icon: Icon, ...rest }) => {
                  const label =
                    "labelKey" in rest ? t(rest.labelKey) : rest.label;
                  const active = authProviders.includes(key);
                  return (
                    <span
                      key={key}
                      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium ${
                        active
                          ? "border-gray-300 bg-gray-100 text-gray-700"
                          : "border-gray-200 bg-gray-50 text-gray-400"
                      }`}
                    >
                      {active ? <Check size={11} /> : <Icon size={10} />}
                      {label}
                    </span>
                  );
                })}
              </div>
            </div>
          </div>
          <div className="mx-4 border-t border-[#e3e8f0]" />

          {/* サブスクリプション */}
          <div className="flex items-center justify-between gap-4 px-4 py-4">
            <div>
              <p className="text-sm text-gray-900">
                {t("settings:subscription.title")}
              </p>
              <p className="text-[13px] text-gray-500">
                {isPro
                  ? t("settings:subscription.active")
                  : waitlist?.onWaitlist
                    ? t("settings:subscription.waitlistRegistered")
                    : t("settings:subscription.upgradeDescription")}
              </p>
            </div>
            {!isPro && !waitlist?.onWaitlist && (
              <button
                type="button"
                onClick={() => navigate("/paywall")}
                className="min-h-10 shrink-0 text-sm font-bold text-[#285bbf]"
              >
                {t("settings:subscription.joinWaitlist")}
              </button>
            )}
          </div>
        </section>

        {/* グループ */}
        <SectionHeader>{t("settings:groups.title")}</SectionHeader>
        <section className="overflow-hidden rounded-[20px] border border-[#e3e8f0] bg-white">
          {groups.length > 0 ? (
            groups.map((group) => (
              <button
                type="button"
                key={group.id}
                onClick={() => navigate(`/groups/${group.id}`)}
                className="flex min-h-[52px] w-full items-center justify-between border-b border-[#e3e8f0] px-4 py-3 text-left transition-colors last:border-b-0 hover:bg-[#f0f3f8]"
              >
                <span className="text-sm text-gray-900">{group.name}</span>
                <ChevronRight size={16} className="text-gray-400" />
              </button>
            ))
          ) : (
            <p className="px-4 py-3 text-sm text-gray-500">
              {t("settings:groups.noGroups")}
            </p>
          )}
          <button
            type="button"
            onClick={() => navigate("/groups/new")}
            className="flex min-h-[52px] w-full items-center gap-3 px-4 py-3 text-left text-sm font-bold text-[#285bbf] transition-colors hover:bg-[#edf3ff]"
          >
            +{" "}
            {t("settings:groups.createNew", {
              defaultValue: "新しいグループを作成",
            })}
          </button>
        </section>

        {/* 通知設定 */}
        <SectionHeader>{t("settings:notifications.title")}</SectionHeader>
        <section className="overflow-hidden rounded-[20px] border border-[#e3e8f0] bg-white">
          {notifLoading ? (
            <p className="px-4 py-3 text-sm text-gray-500">
              {t("settings:notifications.loading")}
            </p>
          ) : notifError ? (
            <div className="flex items-center gap-2 px-4 py-3">
              <p className="text-sm text-red-500">
                {t("settings:notifications.error")}
              </p>
              <button
                type="button"
                onClick={fetchNotificationSettings}
                className="inline-flex items-center gap-1 text-sm text-blue-600"
              >
                <RefreshCw size={12} />
                {t("common:buttons.retry")}
              </button>
            </div>
          ) : notifSettings ? (
            <>
              {/* アプリ通知 */}
              <ToggleRow
                title={t("settings:notifications.push.title")}
                description={
                  notifSettings.hasDeviceTokens
                    ? t("settings:notifications.push.descriptionEnabled")
                    : t("settings:notifications.push.descriptionDisabled")
                }
                checked={notifSettings.pushEnabled}
                disabled={!notifSettings.hasDeviceTokens || notifUpdating}
                onChange={(v) => handleToggle("pushEnabled", v)}
              />
              {/* LINE通知 */}
              <ToggleRow
                title={t("settings:notifications.line.title")}
                description={
                  isPro
                    ? t("settings:notifications.line.description")
                    : t("settings:notifications.line.proOnly")
                }
                checked={notifSettings.lineEnabled}
                disabled={!isPro || notifUpdating}
                onChange={(v) => handleToggle("lineEnabled", v)}
              />
              {/* メール通知 */}
              <ToggleRow
                title={t("settings:notifications.email.title")}
                description={
                  !notifSettings.hasEmail
                    ? t("settings:notifications.email.noEmail")
                    : t("settings:notifications.email.description")
                }
                checked={notifSettings.emailEnabled}
                disabled={!notifSettings.hasEmail || notifUpdating}
                onChange={(v) => handleToggle("emailEnabled", v)}
              />
              <div className="mx-4 border-t border-[#e3e8f0]" />
              {/* 通知時間帯 */}
              <div className="px-4 py-4">
                <p className="mb-1 text-sm font-medium text-gray-900">
                  {t("settings:notifications.hours.title")}
                </p>
                <p className="mb-3 text-xs text-gray-500">
                  {t("settings:notifications.hours.description")}
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {SELECTABLE_HOURS.map((hour) => {
                    const selected =
                      notifSettings.preferredHours.includes(hour);
                    return (
                      <button
                        key={hour}
                        type="button"
                        onClick={() => toggleHour(hour)}
                        disabled={hoursSaving}
                        className={`flex h-[34px] w-[46px] items-center justify-center rounded-lg border text-xs font-medium transition-colors ${
                          selected
                            ? "border-[#4e81ee] bg-[#edf3ff] text-[#285bbf]"
                            : "border-[#e3e8f0] bg-white text-gray-500 hover:bg-[#f0f3f8]"
                        }`}
                      >
                        {t("common:units.hours", { count: hour })}
                      </button>
                    );
                  })}
                </div>
              </div>
            </>
          ) : null}
        </section>

        {/* 言語設定 */}
        <SectionHeader>{t("settings:language.title")}</SectionHeader>
        <section className="rounded-[20px] border border-[#e3e8f0] bg-white p-4">
          <div
            role="radiogroup"
            className="grid grid-cols-2 gap-2 sm:grid-cols-3"
          >
            {LANGUAGES.map((lang) => {
              const selected = i18n.language === lang.code;
              return (
                <label
                  key={lang.code}
                  className={`flex min-h-11 cursor-pointer items-center justify-center rounded-[14px] border px-3.5 py-2 text-[13px] font-bold transition-colors ${
                    selected
                      ? "border-[#4e81ee] bg-[#edf3ff] text-[#285bbf]"
                      : "border-[#e3e8f0] bg-white text-gray-700 hover:bg-[#f0f3f8]"
                  }`}
                >
                  <input
                    type="radio"
                    name="language"
                    value={lang.code}
                    checked={selected}
                    onChange={() => handleLanguageChange(lang.code)}
                    className="sr-only"
                  />
                  {lang.label}
                </label>
              );
            })}
          </div>
        </section>

        {/* ログアウト */}
        <div className="pt-2">
          <button
            type="button"
            onClick={handleLogout}
            className="h-[52px] w-full rounded-[14px] border border-[#e3e8f0] bg-white text-sm font-bold text-gray-700 transition-colors hover:bg-[#f0f3f8]"
          >
            {t("settings:logout")}
          </button>
        </div>

        {/* アカウント削除 */}
        <div className="flex justify-center pb-8">
          <button
            type="button"
            onClick={() => setShowDeleteConfirm(true)}
            className="text-xs text-gray-400 transition-colors hover:text-gray-500"
          >
            {t("settings:deleteAccount.button")}
          </button>
        </div>
      </main>

      {/* LINE友だち追加案内 */}
      <BottomSheet
        open={showLineFriendGuide}
        onClose={() => setShowLineFriendGuide(false)}
        title={t("settings:notifications.line.friendGuide.title")}
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            {t("settings:notifications.line.friendGuide.description")}
          </p>
          <a
            href={LINE_FRIEND_ADD_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#06C755] py-3 text-sm font-semibold text-white transition-colors hover:bg-[#05b64e]"
          >
            {t("settings:notifications.line.friendGuide.addFriend")}
          </a>
          <button
            type="button"
            onClick={() => setShowLineFriendGuide(false)}
            className="w-full rounded-lg border border-gray-300 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
          >
            {t("settings:notifications.line.friendGuide.close")}
          </button>
        </div>
      </BottomSheet>

      {/* アカウント削除確認ダイアログ */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl">
            <h2 className="mb-2 text-base font-semibold text-gray-900">
              {t("settings:deleteAccount.confirmTitle")}
            </h2>
            <p className="mb-6 text-sm text-gray-500">
              {t("settings:deleteAccount.confirmDescription")}
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(false)}
                disabled={isDeleting}
                className="flex-1 rounded-lg border border-gray-300 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50"
              >
                {t("settings:deleteAccount.cancelButton")}
              </button>
              <button
                type="button"
                onClick={handleDeleteAccount}
                disabled={isDeleting}
                className="flex-1 rounded-lg border border-red-500 py-2.5 text-sm font-semibold text-red-500 transition-colors hover:bg-red-50 disabled:opacity-50"
              >
                {isDeleting
                  ? t("settings:deleteAccount.deleting")
                  : t("settings:deleteAccount.confirmButton")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SectionHeader({ children }: { children: React.ReactNode }) {
  return (
    <p className="mb-[-12px] px-1 text-xs font-bold text-gray-500">
      {children}
    </p>
  );
}

function ToggleRow({
  title,
  description,
  checked,
  disabled,
  onChange,
}: {
  title: string;
  description: string;
  checked: boolean;
  disabled: boolean;
  onChange: (value: boolean) => void;
}) {
  return (
    <label
      className={`flex min-h-[68px] items-center justify-between border-b border-[#e3e8f0] px-4 py-3 last:border-b-0 ${disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"}`}
    >
      <div className="mr-4">
        <p className="text-sm text-gray-900">{title}</p>
        <p className="text-xs text-gray-500">{description}</p>
      </div>
      <div className="relative shrink-0">
        <input
          type="checkbox"
          className="peer sr-only"
          checked={checked}
          disabled={disabled}
          onChange={(e) => onChange(e.target.checked)}
        />
        <div className="h-6 w-11 rounded-full bg-gray-200 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:bg-white after:transition-all after:content-[''] peer-checked:bg-[#4e81ee] peer-checked:after:translate-x-full peer-disabled:opacity-50" />
      </div>
    </label>
  );
}
